
import React from 'react';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Eye, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
interface PractitionerTableProps {
  practitioners: any[];
  loading: boolean;
  onViewDetails: (practitioner: any) => void;
  getStatusBadge: (status: string) => React.ReactNode;
  getVerificationBadge: (isVerified: boolean) => React.ReactNode;
}

const PractitionerTable: React.FC<PractitionerTableProps> = ({
  practitioners,
  loading,
  onViewDetails,
  getStatusBadge,
  getVerificationBadge,
}) => {
  const { toast } = useToast();

  const handleToggle = async (p: any) => {
    try {
      const { error } = await supabase
        .from('practitioners')
        .update({ accepting_new_clients: !p.accepting_new_clients, updated_at: new Date().toISOString() })
        .eq('id', p.id);
      if (error) throw error;
      toast({
        title: !p.accepting_new_clients ? '已显示在列表' : '已从列表隐藏',
        description: `${p.first_name || ''} ${p.last_name || ''}`.trim() || 'Practitioner',
      });
      // 简单方式：刷新以获取最新数据
      window.location.reload();
    } catch (e) {
      console.error('Toggle accepting_new_clients error:', e);
      toast({ variant: 'destructive', title: '更新失败', description: '请稍后再试。' });
    }
  };
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2 text-primary" />
          <p className="text-muted-foreground">Loading practitioners...</p>
        </div>
      </div>
    );
  }

  if (practitioners.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-center text-muted-foreground">
          <Eye className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <h3 className="text-lg font-medium mb-2 text-foreground">No practitioners found</h3>
          <p>No practitioners found matching your criteria.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Verified</TableHead>
            <TableHead>Registration Date</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {practitioners.map((practitioner) => (
            <TableRow key={practitioner.id}>
              <TableCell className="font-medium">
                {practitioner.first_name} {practitioner.last_name}
              </TableCell>
              <TableCell>{practitioner.email}</TableCell>
              <TableCell>{getStatusBadge(practitioner.profile_status)}</TableCell>
              <TableCell>{getVerificationBadge(practitioner.is_verified)}</TableCell>
              <TableCell>
                {new Date(practitioner.created_at).toLocaleDateString()}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-2">
                  <Button
                    variant={practitioner.accepting_new_clients ? 'secondary' : 'default'}
                    size="sm"
                    onClick={() => handleToggle(practitioner)}
                    title={practitioner.accepting_new_clients ? 'Hide from book page' : 'Show on book page'}
                  >
                    {practitioner.accepting_new_clients ? 'Hide from listing' : 'Show in listing'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onViewDetails(practitioner)}
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    View Details
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default PractitionerTable;
